import { useAppIndexStore } from '@base/stores/indexStore'
import { ECarts, ESpecialPage } from '@base/utils/constants'

import type * as tpWilo from '~/utils/types'
import type { IItemState, IPriceObj } from '~/utils/types'
import { ut_getCountryValue, ut_getFilterDescr, ut_getObjectByPath, ut_getSelectedFromPsx, ut_getText, ut_getValuesByPath, ut_isEmbedded, ut_stripFootNote } from '@base/utils/utils'
import type * as pf from '@amirada/lib-shared'
import { DataType, NodeType } from '@amirada/lib-shared'
import { EItemState, EUserUserDefined, FlowUnits, HeadUnits, PressureUnits, def_coverPicsRoot, def_empCoCountries, def_internalHosts, def_internalUserAllowlist, def_marketLanguages, def_priceListDescriptionComps, def_printCompareGroupSize, def_salesInfoOptions, def_seriesPriceGroupCods, def_testAccountId, def_wiloWorldlist } from '~/utils/constants'
import type * as pfWilo from '@amirada/lib-wilo-shared'
import { ELogicGroup, ESubTab, ETab, PARAM } from '@amirada/lib-wilo-shared'
import type * as tp from '@base/utils/types'

// Describe a psx as one line per filter — "<filter>: <value>, <value>" — for a heading above the
// table it produced. ut_getFilterDescr describes a single selection item, so two values of one
// filter would repeat the filter name, which reads wrong as a heading. A slider carries its whole
// range in a single item and is therefore taken as it comes, name included.
export function ut_wilo_getFilterLines(psx: string, filterNodes: pf.TPfNode[]): string[] {
  const byCombo: Record<string, string[]> = {}
  for (const item of ut_getSelectedFromPsx(psx)) {
    const combo: string = item.split('|')[0] || ''
    if (!combo) { continue }
    if (!byCombo[combo]) { byCombo[combo] = [] }
    byCombo[combo].push(item)
  }
  const res: string[] = []
  for (const [combo, items] of Object.entries(byCombo)) {
    const single: string | undefined = items.length === 1 ? items[0] : undefined
    if (single) {
      const line: string = ut_getFilterDescr(single, filterNodes, true)
      if (line) { res.push(line) }
      continue
    }
    const node: pf.TPfNode | undefined = filterNodes.find(n => n.nodeName === combo)
    const values: string[] = items.map(item => ut_getFilterDescr(item, filterNodes, false)).filter(Boolean)
    if (node && values.length) { res.push(ut_stripFootNote(node.desc) + ': ' + values.join(', ')) }
  }
  return res
}

// Get Wilo picture in different sizes
export function ut_wilo_pictureSize(src: string, number: number): string {
  return src.replace('_1.', '_' + number.toString() + '.')
}

// A saved pdf cover as a URL: a standard cover is a path below def_coverPicsRoot, the cover of a Wilo
// template a full URL.
export function ut_wilo_coverSrc(value: string): string {
  return /^https?:\/\//.test(value) ? value : def_coverPicsRoot + value
}

// Filter and format price values
export function ut_wilo_filterPrice(prop: pf.TPfNode | undefined, value: string, useLink: boolean = false, countryLanguage?: string, prodDesc?: string, articleNo?: string): string {
  const indexStore = useAppIndexStore()
  let res: string = ''
  // '-' is how the data says "no price" — not JSON. Parsing it only filled the server log with
  // "Error parsing price JSON" (14,243 times in one LIVE week, 18.–25.09.2026) and ended in the same
  // "price on request" as below.
  if (value && value.trim() !== '-') {
    try {
      const json: IPriceObj[] = JSON.parse(value || '{}')
      if (json) {
        const date: string = (indexStore.state.paramData.queryUrlServer.date || '').split('|')[0] || ''
        const today: number = !date ? new Date().getTime() : new Date(date).getTime()
        for (let i = 0; i < json.length; i++) {
          if (Date.parse(json[i]?.begin || '') <= today && Date.parse(json[i]?.until || '') > today - 86400000) {
            const price: number = +(json[i]?.price || 0)
            // normal prices must be > 1, french exo contribution > 0
            if (price > 1 || (price > 0 && (prop && prop.attrs['SourceFeature'] && prop.attrs['SourceFeature'].startsWith('@eco_')))) {
              const formatter = new Intl.NumberFormat(indexStore.state.paramData.iso639 || 'de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              let formattedNumber = formatter.format(price)
              // remove trailing 00 and replace with -
              const fraction: string = formattedNumber.substring(formattedNumber.length - 2, formattedNumber.length)
              if (fraction === '00') {
                formattedNumber = formattedNumber.substring(0, formattedNumber.length - 2) + '-'
              }
              res = (json[i]?.currency || '') + ' ' + formattedNumber
            }
            break
          }
        }
      }
    }
    catch (err) {
      res = ''
      const e = err as Error
      console.error('Error parsing price JSON', value, prodDesc, articleNo, e.message)
    }
  }
  // Price on request
  if (res === '') {
    const onRequestText: string = ut_getText('txt_on-request')
    if (countryLanguage && useLink) {
      res = '<a href=\'' + 'https://wilo.com/' + countryLanguage + '/Price-request-form/?productdescription=' + prodDesc + '&articlenumber=' + articleNo + '\'>' + onRequestText + '</a>'
    }
    else {
      res = onRequestText
    }
  }
  return res
}

/*
 * Who owns a saved project — and the single place that answers it.
 *
 * Normally the MSAL account (`Msal.vue` puts `homeAccountId` there); `groupId` on
 * CART_CREATE/CART_LIST is built from it. Where nobody is signed in, a fixed id stands in, so the
 * price list can be saved and found again rather than failing at nothing.
 *
 * ⚑ It has to be ONE function, not the same computed in two components. `projectAutoSave` SAVES
 * under this id and `CartManage` LISTS by it; if those two ever disagree, saving appears to work
 * and the project is simply never shown again.
 *
 * ⚑ What this costs, stated plainly (decision of 25.08.2026): everyone without a sign-in shares one
 * bucket and sees the same projects. That is deliberate — the alternative was the previous
 * behaviour, where an unsigned session built a whole price list and lost it without a single log
 * line, which is the worse of the two.
 */
export function ut_wilo_accountId(): string {
  return useAppIndexStore().state.paramData.userData.accountId || def_testAccountId
}

// Extract real country from historic country code
export function ut_wilo_realCountry(isHistoric: boolean): string {
  const indexStore = useAppIndexStore()
  return isHistoric ? indexStore.state.paramData.rcco.slice(0, -2) : indexStore.state.paramData.rcco
}

// Push Adesso CMS to update cart info
export function ut_wilo_setAdessoStorage(cart: ECarts, cartCount: number, country: string): void {
  if (ut_isEmbedded()) {
    const listName: string = cart
    localStorage.setItem(country + '_' + 'listCount_' + listName, cartCount.toString())
    const onListChange = new Event('listCountChanged')
    window.dispatchEvent(onListChange)
  }
}

/*
 * The one tab that is DISPLAYED without its subtab level: range_advantages when nothing but
 * range_sub_advantages is left under it. Both carry the same label, so an accordion row inside a
 * row of the same name would say the same thing twice.
 *
 * ⭐ It collapses the DISPLAY and nothing else. The value stays `tab|subtab`, because that is the
 * structure the server delivers and an external api reads — a tab without its subtab exists nowhere
 * but in this one label. Before 01.09.2026 the flattening reached into the value as well, and a
 * price list then printed the advantages of every series that happened to have no accessories: the
 * flat `range_advantages` matched `range_advantages|range_accessory` as a SUBSTRING in the tab
 * parameter (which travels as raw url text, not as a list).
 *
 * ⚑ Deliberately narrow, and meant to stay the only one: a tab with a single subtab normally keeps
 * both levels. Asked in ONE place because the same decision is needed twice — on the detail page and
 * in the pdf modal — and two copies of it are what let the two drift apart.
 */
export function ut_wilo_isFlattenedTab(tabPsm: string, subTabPsms: string[]): boolean {
  return tabPsm === ETab.rangeAdvantages && subTabPsms.length === 1 && subTabPsms[0] === ESubTab.rangeSubAdvantages
}

// Check if tab should be visible
export function ut_wilo_showTab(tabName: string): boolean {
  let res: boolean = true
  if (tabName) {
    if ([ETab.productDefault.toString(), ETab.rangeDefault.toString(), ETab.productAdditional.toString(), ETab.rangeAdditional.toString()].includes(tabName)) {
      res = false
    }
  }
  return res
}

/**
 * The part of an object the tab rules read: its type, its caption, the two attributes below and its
 * nested tabs.
 *
 * ⚑ Why a type of its own: since the server collects the tabs of a whole cart (cmdTabs, 01.09.2026)
 * the rules are asked about TWO shapes — a real detail node on the detail page, and the server's
 * pruned projection in the PDF dialog. Both satisfy this, so the SAME rules apply to both without a
 * cast and without a second copy of them. See ai_memory/project_tab_collection_cmdtabs.md.
 */
export interface ITabSource {
  nodeType: number
  desc: string
  attrs: Record<string, string>
  nodes?: ITabSource[]
}

// Check if sub tab should be visible
export function ut_wilo_showSubTab(tabName: string, detailData: ITabSource, rcco: string, isHistoric: boolean): boolean {
  let res: boolean = true
  if (detailData.attrs) {
    if (isHistoric && tabName === ESubTab.productSalesdata) {
      res = false
    }
    if (rcco !== '_preview' && tabName === ESubTab.productPumpcurve && (detailData.attrs['OEM'] && (detailData.attrs['OEM'] === '1'))) {
      res = false
    }
    if (tabName === ESubTab.productPumpcurve && !detailData.attrs['curve']) {
      res = false
    }
  }
  return res
}

// Get descriptions for special pages Sys3 => Sys4
export function ut_wilo_getSpecialPageText(name: string): string {
  switch (name) {
    case ESpecialPage.sp_shopping_cart:
      return 'shoppingcart'
    case ESpecialPage.sp_product_compare:
      return 'productcompare'
    case ESpecialPage.sp_download_list:
      return 'downloadlist'
    case ESpecialPage.sp_favourites:
      return 'favorites'
    case ESpecialPage.sp_catalogue_create:
      return 'catalogcreate'
    case ESpecialPage.sp_price_list_create:
      return 'pricelistcreate'
    case ESpecialPage.sp_custom:
      return 'LCC'
    default:
      return ''
  }
}

// The media rule (marketRestriction / mediaLanguages) lived here until 01.09.2026 and is gone: the
// SERVER filters those values now (pfinder-wilo isAllowedMediamid), so nothing arrives that this
// would have thrown away. Downloads keep their other-language editions on purpose — see there.

// Item state to show as badge
export function ut_wilo_ItemState(salesAvailability: Date, salesDiscontinuation: Date, salesIntroduction: Date, salesExpiration: Date): IItemState {
  const res: IItemState = {
    state: EItemState.st_none,
    date: '',
  }
  const indexStore = useAppIndexStore()
  const date: string = (indexStore.state.paramData.queryUrlServer.date || '').split('|')[0] || ''
  const actualDate: Date = !date ? new Date() : new Date(date)
  const newDate: Date = new Date(new Date().setMonth(actualDate.getMonth() - 12))
  // console.log(salesAvailability, salesDiscontinuation, salesIntroduction,actualDate, newDate)
  if (salesAvailability > salesIntroduction && salesAvailability > actualDate) {
    res.state = EItemState.st_soon
  }
  else if (actualDate >= salesExpiration) {
    res.state = EItemState.st_expired
    res.date = ' ' + salesExpiration.getFullYear().toString()
  }
  else if (actualDate >= salesDiscontinuation) {
    res.state = EItemState.st_discontinued
  }
  else if (salesAvailability >= newDate) {
    res.state = EItemState.st_new
  }
  return res
}

// Item state color to show as badge
export function ut_wilo_ItemStateColor(itemState: EItemState): string {
  return itemState === EItemState.st_new || itemState === EItemState.st_soon ? 'bg-primary' : 'bg-gunmetal'
}

// Is this an EmpCo market — texts that may not comply with the EU EmpCo directive are hidden there
// for now (def_empCoCountries). Takes the REAL country (ut_wilo_realCountry): the historic markets
// carry `_H`, and they are the same market. The rcco is always lower case in the client, so is the list.
export function ut_wilo_isEmpCo(realCountry: string): boolean {
  return def_empCoCountries.includes(realCountry)
}

/*
 * The text search of the price list's catalog modal (24.09.2026): keep the series whose name contains
 * the text, case ignored. One function for the tiles (List) and for what the modal counts, suggests and
 * "adds all shown" (CatalogView), so the four cannot disagree. The name is node.desc — the same text
 * the tile shows (first list column). An empty text keeps everything.
 */
export function ut_wilo_filterByText<T extends { desc?: string }>(nodes: T[], text: string): T[] {
  const term: string = text.trim().toLowerCase()
  if (!term) { return nodes }
  return nodes.filter(node => (node.desc || '').toLowerCase().includes(term))
}

// Check if is ECOM product
export function ut_wilo_isEcom(attrs: Record<string, string> | undefined, realCountry: string): boolean {
  if (attrs && attrs['ECOM']) {
    return (';' + attrs['ECOM'] + ';').toLowerCase().includes(';' + realCountry + ',') && ('|' + useAppConfig().ecoMarCountries + '|').includes('|' + realCountry + '|')
  }
  else {
    return false
  }
}

// Wholesaler or ECOM shopping links
export function ut_wilo_shoppingLinks(node: pf.TPfNode, realCountry: string): tpWilo.IShoppingLink[] {
  const linkPathDirect: any = {
    ro: 'https://ro.shop.wilo.com/ro/',
  }

  const res: tpWilo.IShoppingLink[] = []
  if (ut_wilo_isEcom(node.attrs, realCountry)) {
    res.push({
      name: node.attrs?.id_product || '',
      link: linkPathDirect[realCountry] + (node.attrs?.id_product || ''),
      isEcom: true,
    })
  }
  else {
    const path: string = node.nodeType === NodeType.nt_Configuration ? ETab.rangeDefault + '/' + ELogicGroup.rangeDefaultAdditional : ETab.productDefault + '/' + ELogicGroup.productAdditional
    const data: pf.TPfNode = ut_getObjectByPath(node, path) as pf.TPfNode
    if (data && data.props) {
      const props: pf.TPfNode[] = data.props.filter(e => e.cod.startsWith('Link_wholesalers_' + realCountry))
      for (const prop of props) {
        if (prop.values && prop.values[0]) {
          res.push({
            name: prop.cod,
            link: prop.values[0].desc,
            isEcom: false,
          })
        }
      }
    }
  }
  return res
}

// Return sales values for detail page
export function ut_wilo_salesValue(comps: pf.TPfNode[], name: string, combine: boolean, takeValue: boolean, countryLanguage?: string, prodDesc?: string, articleNo?: string): string {
  let res: string = ''
  for (const comp of comps) {
    if (comp.props) {
      const props: pf.TPfNode[] = comp.props.filter(e => e.nodeName === name)
      if (props.length === 1) {
        const prop: pf.TPfNode = props[0] as pf.TPfNode
        if (takeValue || combine) {
          if (prop.values && prop.values[0]) {
            let value: string = prop.values[0].desc
            if (ut_wilo_isPriceProp(prop) && countryLanguage && prodDesc) {
              value = ut_wilo_filterPrice(prop, prop.values[0].desc, true, countryLanguage, prodDesc, articleNo)
            }
            res = combine ? prop.desc + ': ' + value : value
          }
        }
        else {
          res = prop.desc
        }
      }
    }
  }
  return res
}

/*
 * Check if is price prop.
 *
 * Two spellings, because prices arrive in two shapes: a list carries its own `oc_price_*` props, the
 * accessory data (`meta.acc`) has a single column called `price_STD`. Everything that decides
 * something ABOUT a price asks here — hiding the column, formatting the value — so the next price
 * field is entered in this one place instead of in every consumer.
 */
export function ut_wilo_isPriceProp(prop: pf.TPfNode): boolean {
  return prop.cod.includes('oc_price_') || prop.cod === 'price_STD'
}

// Filter price for list values
export function ut_wilo_parseListValue(prop: pf.TPfNode, value: string, noPrice: boolean): string {
  let res: string = value
  if (Number(prop.attrs?.CFF) === 2) {
    if (ut_wilo_isPriceProp(prop)) {
      res = noPrice ? '-' : ut_wilo_filterPrice(prop, value || '')
    }
  }
  return res
}

// Get extra keys
export function ut_wilo_getExtraKeys(node: pf.TPfNode | pf.TPfNode[] | undefined): string[] {
  let res: string[] = []
  if (node) {
    // Compare table
    if (Array.isArray(node)) {
      for (const node2 of node) {
        res = getExtraKeys(node2, res)
      }
    }
    // Single product
    else {
      if (node.nodes) {
        for (const node2 of node.nodes) {
          res = getExtraKeys(node2, res)
        }
      }
    }
  }
  return res
}

// Filter extra keys and push to result
function getExtraKeys(node: pf.TPfNode, res: string[]): string[] {
  if (node.nodeType === NodeType.nt_Compound && node.props && node.cod !== 'Variable_criteria') {
    for (const prop of node.props) {
      if (prop.attrs?.Software_supplier && (prop.attrs.Software_supplier === 'VSX') && prop.attrs.select_4_formula) {
        if (!res.includes(prop.attrs['select_4_formula'])) {
          if (prop.attrs['select_4_formula'] !== '{PKCODA.WAEHR}') {
            res.push(prop.attrs['select_4_formula'])
          }
        }
      }
    }
  }
  return res
}

// Get curve ID from attributes
export function ut_wilo_getCurveId(attrs: Record<string, string> | undefined): string {
  return attrs ? attrs['curve'] || '' : ''
}

// Get Stand by pump from attributes
export function ut_wilo_getStandByPump(node: pf.TPfNode): boolean {
  const value: string = ut_getValuesByPath(node, [ETab.productDefault, ETab.productAdditional, 'standby_pump_possible'].join('/'))
  return value === 'yes'
}

// Init default spaix units
export function ut_wilo_getSpaixDefaultUnits(realCountry: string): pfWilo.TSelectedUnits {
  return {
    UnitHead: realCountry === 'us' ? HeadUnits[1] || '' : HeadUnits[0] || '',
    UnitFlow: realCountry === 'us' ? FlowUnits[6] || '' : FlowUnits[0] || '',
    UnitPressure: realCountry === 'us' ? PressureUnits[2] || '' : PressureUnits[1] || '',
  }
}

// Init default dutypoint
export function ut_wilo_getSpaixDefaultDutyPoint(): pfWilo.TDutyPoint {
  return {
    Flow: 0,
    Head: 0,
    StaticHead: 0,
    HeadAsPressure: 0,
  }
}

// Modify compare table to groups of compare tables for print.
// ⚑ Whoever calls this owes every group after the first a `startIndex` of
// `index * def_printCompareGroupSize` — see the constant.
export function ut_wilo_compareTablePrintGroups(table: pfWilo.TableNode | pf.TPfNode | undefined, maxItems: number = def_printCompareGroupSize): pfWilo.TableNode[] | pf.TPfNode[] {
  const tableGroups: any[] = []
  if (table && table.nodes) {
    const parts: number = Math.ceil((table.nodes.length) / maxItems)
    for (let i = 0; i <= parts - 1; i++) {
      const newTable = { ...table }
      newTable.nodes = []
      for (let j = 0; j <= maxItems - 1 && ((i * maxItems) + j) <= table.nodes.length - 1; j++) {
        // @ts-expect-error lib shared problem
        newTable.nodes.push(table.nodes[(i * maxItems) + j])
      }
      tableGroups.push(newTable)
    }
  }
  return tableGroups
}

// Get frequency from country
export function ut_wilo_getFrequency(): string {
  let res: string = '50hz'
  const indexStore = useAppIndexStore()
  // Frquency is set manually
  if (indexStore.state.paramData.queryUrlServer.rcre1) {
    res = indexStore.state.paramData.queryUrlServer.rcre1
  // Read country default
  }
  else {
    // Modify to 60hz
    if (indexStore.state.paramData.rcco === '_international_60hz') {
      res = '60hz'
    }
    else {
      const values: pf.TPfValue[] = ut_getCountryValue('restriction1ref')
      if (values[0]) {
        res = values[0].cod.toLowerCase()
      }
    }
  }
  return res
}

export function ut_wilo_modifyQueryDate(): void {
  const date = new Date()
  const paramsQuery: tp.IParamQueryServer = {
    date: date.toISOString().slice(0, 10) + '|',
  }
  const indexStore = useAppIndexStore()
  indexStore.setModifiedQueryParams(paramsQuery)
}

// Detect the internal Wilo client (solutions.wilo.com or the test-mode override).
// Uses the request host, so it works on both server and client.
export function ut_wilo_isInternal(): boolean {
  const host = useRequestURL().host
  return def_internalHosts.includes(host) || useRuntimeConfig().public.isInternal
}

// Same question answered from paramData alone, for the paths that have no Nuxt instance to bind a
// composable to — customerBuildCustom feeds the stores' cache keys and therefore runs on every read.
//
// ⚑ Without the NUXT_PUBLIC_IS_INTERNAL build override on purpose. That flag is fixed for a whole
// deployment, so it cannot tell two requests of one session apart — and telling requests apart is
// the only thing a cache key is for. An internal dev build therefore keys as an external one, and
// keys as one CONSISTENTLY, which is what matters. The request itself still gets the override, added
// in customerModifyQuery.
export function ut_wilo_isInternalParamData(paramData: tp.IParamData): boolean {
  return def_internalHosts.includes(paramData.host) || ut_wilo_isInternalPrint(paramData)
}

// TEMP (testing): whether the logged-in user's MSAL email is on the internal allowlist.
// Gates, when embedded, the new range subtabs (Detail, ModalPDF) and the Digital product pass link
// (Navigation) — see def_internalUserAllowlist. Remove after testing (release for all internal users
// waits for the customer's answer, 14.10.2026).
export function ut_wilo_isAllowlistedUser(): boolean {
  const eMail = (useAppIndexStore().state.paramData.userData?.eMail || '').toLowerCase()
  // Both sides lowercased, not only the incoming address. Until 07.09.2026 this was an includes()
  // against the list as written, so an entry carrying capitals matched nobody - and that failure is
  // invisible, because "not on the list" is a perfectly ordinary answer. At nine entries the cost of
  // not leaning on the convention is nil.
  return def_internalUserAllowlist.some(entry => entry.toLowerCase() === eMail)
}

// Whether the logged-in user's MSAL email is on the Wilo World list — those users get the Wilo World
// import parameter on level2 application pages. Same check as ut_wilo_isAllowlistedUser.
export function ut_wilo_isWiloWorldUser(): boolean {
  const eMail = (useAppIndexStore().state.paramData.userData?.eMail || '').toLowerCase()
  return def_wiloWorldlist.some(entry => entry.toLowerCase() === eMail)
}

// Internal print (PDF): param9=1 in the request query. Read from the passed paramData, NOT the store —
// this runs from customerModifyQuery during fetchData, before setParamDataMutation fills the store.
export function ut_wilo_isInternalPrint(paramData: tp.IParamData): boolean {
  return paramData[PARAM.internalPrint] === '1'
}

// This page is being rendered as part of a price list (param16). It decides what the page shows:
// the fixed, reduced content the price list asks for instead of the full detail page.
//
// ⚑ On screen this is never true HERE. CartSeriesPriceTable sets param16 only on its own local
// paramData copy — for the requests, not for the page. In the store it arrives from the url, and
// only the headless print run opens such a url. Which is exactly the intent: what hangs off this
// belongs to the PDF. The modal itself knows the same context from its isPriceList prop.
export function ut_wilo_isPriceList(): boolean {
  return String(useAppIndexStore().state.paramData[PARAM.priceList] ?? '') === '1'
}

/*
 * The sales filter of the page being rendered: which of price, price group and delivery time the
 * pdf was asked to leave out (PARAM.salesFilter, written by ModalPDF here and by initObject in the
 * server's pdf.ts).
 *
 * ⭐ An unreadable value CLOSES, it does not open. A non-empty salesFilter exists for exactly one
 * reason — somebody asked for a restriction — so "I cannot read what was restricted" must not end
 * as "then show everything". A missing price is loud and gets reported; a price in a document whose
 * author unticked the box is silent and still reaches the customer.
 *
 * ⚑ That is deliberately the opposite direction from the server's isListColumnVisible, where a
 * wrongly hidden column loses customer data and doubt therefore shows. Here doubt says too much.
 *
 * The ABSENT parameter is the ordinary case, not the doubtful one: every screen url has no
 * salesFilter at all, and that means no restriction.
 */
export function ut_wilo_salesFilter(): pfWilo.ISalesFilter {
  const raw: string = String(useAppIndexStore().state.paramData[PARAM.salesFilter] ?? '')
  if (!raw) { return {} }
  try {
    const parsed: unknown = JSON.parse(raw)
    // JSON.parse hands back `any`, and this value comes off a url — the type protects the writers,
    // never this side. Anything that is not a plain object is treated as unreadable.
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) { throw new Error('not an object') }
    return parsed as pfWilo.ISalesFilter
  }
  catch {
    return { noPrice: true, noPriceGroup: true, noDelivery: true }
  }
}

/*
 * Is this property suppressed by the sales filter?
 *
 * `isOld` folds in the rule of the product list: a discontinued item shows no price — and since
 * 09.09.2026 no price group either, so the two cannot say different things about the same row. In a
 * freshly built price list the combination cannot occur (the templates resolve with `date=now`), but
 * a project saved in September and printed in December can carry a series discontinued meanwhile.
 *
 * ⚑ The price is answered by ut_wilo_isPriceProp, unchanged — that function also drives formatting.
 * The other two are matched against their own exact cod lists; see def_salesInfoOptions.
 */
export function ut_wilo_isSalesPropHidden(prop: pf.TPfNode, isOld: boolean = false): boolean {
  const filter: pfWilo.ISalesFilter = ut_wilo_salesFilter()
  if (ut_wilo_isPriceProp(prop)) { return isOld || !!filter.noPrice }
  for (const option of def_salesInfoOptions) {
    if (option.cods.includes(prop.cod)) { return isOld || !!filter[option.flag] }
  }
  return false
}

/*
 * The price groups of a SERIES, as "<desc>: <value>" lines above its product tables.
 *
 * They sit on the series itself (range_default/range_additional), not in the list, so they cannot
 * be a column — a line above the table is where a statement about the whole series belongs, next to
 * the filter that produced it.
 *
 * ⚑ Price list and pdf only. `ut_wilo_isPriceList()` is BOTH conditions in one: param16 reaches the
 * store from the url alone, and only the headless print run opens such a url. A second `ut_isPrint()`
 * would suggest there is a case where the two differ.
 *
 * Whether they may be shown is the ordinary sales-filter question, asked per property — the two cods
 * are part of the price-group family, so no rule of their own decides it.
 *
 * ⚑ Props are read from the node AND from one level of children: the two structures this data
 * arrives in (flat, or one compound below) are the same pair TabAccessoriesServices deals with.
 *
 * ⚑ TEMPORARY (15.09.2026): a label containing "_" is a COD, not a description — either `desc` is
 * missing and the cod stood in, or the description itself still carries the cod. The customer's
 * descriptions are still being written, so such a label reads "Price group" for now instead of
 * `sales_price_category_series: B`. Remove once the descriptions are maintained.
 */
export function ut_wilo_seriesPriceGroupLines(detailNode: pf.TPfNode | undefined): string[] {
  if (!detailNode || !ut_wilo_isPriceList()) { return [] }
  // ⚑ The logic group is `range_default_additional`, NOT `range_additional` — measured in the
  // delivered tree on 09.09.2026, where range_default holds graphics, texts and additional. There
  // IS an ETab.rangeAdditional, which is a different thing and a tab of its own; taking it here
  // resolves to nothing and the lines simply never appear.
  const node: pf.TPfNode | undefined = ut_getObjectByPath(detailNode, [ETab.rangeDefault, ELogicGroup.rangeDefaultAdditional].join('/'))
  if (!node) { return [] }

  const props: pf.TPfNode[] = [...(node.props || []), ...(node.nodes || []).flatMap(child => child.props || [])]
  const res: string[] = []
  for (const cod of def_seriesPriceGroupCods) {
    const prop: pf.TPfNode | undefined = props.find(item => item.cod === cod)
    if (!prop || ut_wilo_isSalesPropHidden(prop)) { continue }
    const value: string = (prop.values || []).map(item => item.desc).filter(desc => !!desc).join(', ')
    const label: string = prop.desc || cod
    if (value) { res.push((label.includes('_') ? 'Price group' : label) + ': ' + value) }
  }
  return res
}

// Modify units for request
export function ut_wilo_modifySpaixUnitsForRequest(selectedUnits?: pfWilo.TSelectedUnits): pfWilo.TConfigOption[] {
  const res: pfWilo.TConfigOption[] = []
  if (selectedUnits?.UnitFlow) { res.push({ SpaixUnit: 'UnitFlow', SelectedID: selectedUnits?.UnitFlow }) }
  if (selectedUnits?.UnitHead) { res.push({ SpaixUnit: 'UnitHead', SelectedID: selectedUnits?.UnitHead }) }
  if (selectedUnits?.UnitPressure) { res.push({ SpaixUnit: 'UnitPressure', SelectedID: selectedUnits?.UnitPressure }) }
  return res
}

export function ut_wilo_filteredCountryData(countryData: tp.ISelectItem[] | undefined): tp.ISelectItem[] | undefined {
  return countryData ? countryData.filter(i => !i.value.endsWith('_h')).sort((a, b) => a.label.localeCompare(b.label)) : undefined
}

// Restrict the language list to the ones the current market offers (def_marketLanguages). The
// market comes from the store, never from the URL. This only FILTERS: the order the server
// delivers stays untouched, and a market without an entry keeps the full list.
// No historic (`_h`) handling on purpose — the restricted markets have no historic variant.
export function ut_wilo_filteredLangData(langData: tp.ISelectItem[] | undefined): tp.ISelectItem[] | undefined {
  const allowed: readonly string[] | undefined = def_marketLanguages[useAppIndexStore().state.paramData.rcco]
  return langData && allowed ? langData.filter(i => allowed.includes(i.value)) : langData
}

export function ut_wilo_modifyCountry(rcco: string, isHistoric: boolean): string {
  return isHistoric ? rcco + '_h' : rcco
}

export function ut_wilo_showPriceList(isInternal: boolean): boolean {
  return (isInternal || !ut_isEmbedded()) && useAppIndexStore().state.userDefined[EUserUserDefined.user_us_priceList]
}

/*
 * The description blocks of a series, ready to render.
 *
 * Lives here rather than in `TabRangeDescription.vue` because the price list needs the same content
 * in a second place: its series header shows construction type and type key, while the tab itself is
 * gone from `def_priceListTabs`. There is no way to reach those values directly — they hang under
 * the components of the range_description node — so both callers walk the same path, and they have
 * to walk it the same way.
 *
 * A price list takes a POSITIVE list of components, everything else the whole tab: a block someone
 * adds in CMI cannot turn up by itself in a document that carries prices.
 *
 * ⚑ Identified by `cod`. A renamed cod makes its block MISS, silently — there is no channel to
 * report it from inside a print render. If construction type or type key is absent from a price
 * list, that list is where to look, not the import.
 */
export function ut_wilo_seriesDescriptionComps(node: pf.TPfNode | undefined): pf.TPfNode[] {
  const comps: pf.TPfNode[] = node?.nodes?.[0]?.nodes || []
  return ut_wilo_isPriceList() ? comps.filter(comp => def_priceListDescriptionComps.includes(comp.cod || '')) : comps
}

/*
 * One component as HTML: memo values are taken as they are, everything else becomes a labelled list
 * item. The list is opened and closed around runs of non-memo props, so a memo in between ends the
 * current list instead of swallowing it.
 */
export function ut_wilo_seriesDescriptionHtml(comp: pf.TPfNode): string {
  let res: string = ''
  let part: string = ''
  if (comp.props) {
    for (const prop of comp.props) {
      if (prop.values && prop.values[0]) {
        if (prop.dataType === DataType.dt_Memo) {
          if (part) {
            res += '<ul>' + part + '</ul>'
            part = ''
          }
          res += prop.values[0].desc
        }
        else {
          part += '<li>' + prop.desc + ': ' + prop.values[0].desc + '</li>'
        }
      }
    }
  }
  if (part) {
    res += '<ul>' + part + '</ul>'
  }
  return res
}

/*
 * Every block of every component, flattened into reading order.
 *
 * `headingTag` is the level the block headings are written at. The description tab keeps h4; the
 * price list's series header asks for h5, because there the blocks sit under the series name and one
 * step below it is what they are. ⚑ Whatever is passed needs its `break-after: avoid` in the
 * `.css-description` rule, or the heading can be left standing alone at the foot of a page.
 */
export function ut_wilo_seriesDescriptionParts(node: pf.TPfNode | undefined, headingTag: string = 'h4'): string[] {
  return ut_wilo_seriesDescriptionComps(node)
    .flatMap(comp => ut_wilo_splitDescription(comp, ut_wilo_seriesDescriptionHtml(comp), false, headingTag))
}

/*
 * Only the construction type ("Bauart", cod `Design`) of a series, ready to render — the same block
 * as in the price list's series header (ut_wilo_seriesDescriptionParts), but without its heading.
 *
 * TEMPORARY (24.09.2026): in EmpCo markets it stands in for the promotional subline and the
 * advantages list of the series header (Detail.vue, ut_wilo_isEmpCo). Same ⚑ as there: identified
 * by `cod`, a rename makes it miss silently.
 */
export function ut_wilo_seriesDesignParts(node: pf.TPfNode | undefined): string[] {
  const comps: pf.TPfNode[] = node?.nodes?.[0]?.nodes || []
  return comps
    .filter(comp => comp.cod === 'Design')
    .flatMap(comp => ut_wilo_splitDescription(comp, ut_wilo_seriesDescriptionHtml(comp), true))
}

export function ut_wilo_splitDescription(comp: pf.TPfNode, str: string, noHeader: boolean, headingTag: string = 'h4'): string[] {
  const splitStr: string = '<P><B>'
  const res: string[] = []
  if (str.includes(splitStr) && comp.cod !== 'Typekey') {
    const partAr = str.split(splitStr)
    for (const part of partAr) {
      if (str.substring(0, 6) === splitStr || res.length > 0) {
        res.push(splitStr + part)
      }
      else {
        res.push(part)
      }
    }
    if (!noHeader) {
      res[0] = '<' + headingTag + '>' + comp.desc + '</' + headingTag + '>'
    }
  }
  else {
    res.push(str)
    if (!noHeader) {
      res[0] = '<' + headingTag + '>' + comp.desc + '</' + headingTag + '>' + res[0]
    }
  }
  return res
}

export async function ut_wilo_addItemToCart(cartStore: any, cart: ECarts, isConfig: boolean, namePath: string): Promise<void> {
  let confNamePath: string = ''
  if (!isConfig) {
    const confNameAr: string[] = namePath.split('/')
    confNameAr.pop()
    confNamePath = confNameAr.join('/')
    // Add also config to cart for special carts
    if (cart === ECarts.ct_favourites || cart === ECarts.ct_catalogue_create) {
      if (!cartStore.getIsInCart(cart, confNamePath, '')) {
        await cartStore.setAddItemToCart(cart, confNamePath, 1)
      }
    }
  }
  const position: number = ut_wilo_findPositionForCart(cartStore, cart, isConfig ? namePath : confNamePath, isConfig)
  await cartStore.setAddItemToCart(cart, namePath, 1, position)
}

export function ut_wilo_findPositionForCart(cartStore: any, cart: ECarts, confNamePath: string, isConfig: boolean): number {
  const fullCart: pf.CartItem[] = cartStore.getCart(cart)
  let position: number
  // Shopping cart
  if (cart === ECarts.ct_shopping_cart) {
    position = -1
    // Compare
  }
  else if (cart === ECarts.ct_product_compare) {
    // First all configs
    if (!isConfig) {
      position = -1
      // Configs at the beginning
    }
    else {
      position = fullCart.findIndex(i => +(i.nodeType || 0) !== NodeType.nt_Configuration)
      if (position > 0) {
        position--
      }
    }
    // Favorites, Catalog, Downloads
  }
  else {
    position = fullCart.findIndex(i => (i.namePath === confNamePath || i.config?.namePath === confNamePath))
    if (position > -1) {
      // if item is config put at first place, if product put after first, first can be config
      if (!isConfig) {
        position++
      }
    }
  }

  return position
}

export async function ut_wilo_removeItemFromCart(cartStore: any, cart: ECarts, isConfig: boolean, namePath?: string, nodeName?: string): Promise<void> {
  // Remove single product
  if (!isConfig) {
    cartStore.setRemoveItemFromCart(cart, namePath, nodeName)
  }
  // Remove config including all products
  else {
    const configName: string = namePath ? namePath.split('/').pop() || '' : nodeName || ''
    if (configName) {
      const fullCart: pf.CartItem[] = cartStore.getCart(cart)
      const clonedCart: pf.CartItem[] = [...fullCart]
      const itemList: pf.CartItem[] = []
      for (const item of clonedCart) {
        if (item.namePath.includes(configName)) {
          itemList.push(item)
        }
      }
      await cartStore.setRemoveListFromCart(cart, itemList)
    }
  }
}

/*
 * Estimate the rendered height of a description block, in pixels, from its markup alone.
 *
 * Deliberately an estimate: measuring in the DOM would be exact, but it needs a second layout pass
 * and JS timing that the PDF renderer cannot be relied on for. Being right to about one line per
 * block is enough to decide where to cut a list of blocks in two.
 *
 * Counted are the leaf blocks a description actually consists of - headings, paragraphs and list
 * items - plus any text that sits outside them. `charsPerLine` is what fits on one line at the
 * column width; it is the only value to touch if the split starts landing badly.
 */
export function ut_wilo_estimateBlockHeight(html: string, charsPerLine: number = 90): number {
  const LINE: number = 24 // a paragraph or list item line
  const HEADING: number = 30 // a heading line
  const HEADING_GAP: number = 16 // the space a heading adds below itself
  const textOf = (s: string): string => s.replace(/<[^>]*>/g, ' ').replace(/&[a-z]+;|&#\d+;/gi, ' ').replace(/\s+/g, ' ').trim()
  const linesOf = (s: string): number => Math.max(1, Math.ceil(textOf(s).length / charsPerLine))

  let height: number = 0
  let rest: string = html
  const blocks = html.match(/<(h[1-6]|p|li)\b[^>]*>[\s\S]*?<\/\1>/gi) || []
  for (const block of blocks) {
    rest = rest.replace(block, ' ')
    const isHeading: boolean = /^<h[1-6]\b/i.test(block)
    height += linesOf(block) * (isHeading ? HEADING : LINE) + (isHeading ? HEADING_GAP : 0)
  }
  // Text outside any of those blocks still occupies lines.
  if (textOf(rest)) {
    height += linesOf(rest) * LINE
  }
  return height
}

/*
 * TEMPORARY: split description blocks into columns WITHOUT CSS multi-column, keeping the reading
 * order multi-column has: the first column top to bottom, then the next.
 *
 * ⚑ This is a workaround for a Chrome defect, not the intended way to lay out two columns. It is
 * meant to be REMOVED once Chrome balances this content correctly - the original markup and the
 * test for it are below. Until then it is what keeps the page from scrolling sideways.
 *
 * ⚑ Why not `columns-2`: Chrome's column balancer gives up on this content and opens an OVERFLOW
 * column - a third column outside the container, which makes the whole page scroll sideways.
 * Measured 27.08.2026 on the range description of id1237: the SAME block heights rendered as plain
 * boxes balance correctly, the real content (h4 + p + ul) does not, and no break-inside, height,
 * margin or parent-context change moves it - not even a height at which everything would fit in a
 * single column. Firefox grows the column height instead and looks right. So the split is decided
 * here, in the data, and each column is rendered as an ordinary block flow: identical in every
 * browser and in the PDF, where the same balancer would otherwise run.
 *
 * The markup this replaced, kept here on purpose so it can be checked against a later Chrome:
 *
 *     <div class="lg:columns-2 print:columns-2 gap-8">
 *       <template v-for="comp in descriptionComps" :key="comp.nodeName">
 *         <div v-for="(part, pindex) in ut_wilo_splitDescription(...)" class="mb-4 print:mb-3">
 *
 * To retest: put that back, open a range description whose blocks differ a lot in height (id1237 was
 * the case, 146/30/24/24/24/268 px) and read document.documentElement.scrollWidth against
 * clientWidth - they differ while the third column exists. Broken as of Chrome 151. Even once it is
 * fixed there is no reason to hurry back: the PDFs are rendered by whatever Chrome the server
 * carries, so the return needs that version to be the fixed one too.
 *
 * A block moves to the next column once its MIDDLE would sit past that column's share of the total
 * height - the usual cut heuristic, and the reason a tall last block ends up alone on the right
 * rather than hanging over the bottom of the left.
 */
export function ut_wilo_splitIntoColumns(blocks: string[], columnCount: number = 2): string[][] {
  const columns: string[][] = Array.from({ length: Math.max(1, columnCount) }, () => [])
  if (columnCount < 2 || blocks.length < 2) {
    columns[0] = [...blocks]
    return columns
  }
  const heights: number[] = blocks.map(block => ut_wilo_estimateBlockHeight(block))
  const target: number = heights.reduce((sum, h) => sum + h, 0) / columnCount

  let column: number = 0
  let filled: number = 0
  blocks.forEach((block, index) => {
    // Never leave a column empty: the first block of a column always stays in it.
    if (column < columnCount - 1 && filled > 0 && filled + (heights[index] || 0) / 2 > target) {
      column++
      filled = 0
    }
    columns[column]!.push(block)
    filled += heights[index] || 0
  })
  return columns
}
